import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Anchor, Users, Gauge, Activity, ArrowLeftRight, Truck, Store, SlidersHorizontal, MapPin, Fuel } from 'lucide-react';
import { ListSection, ListRow, PrimaryButton } from '../../components/RowList';
import SubmitBudgetButton from '../../components/SubmitBudgetButton';
import { configService } from '../../services/configService';
import { useAppContext } from '../../context/AppContext';

// Module 4 — Distribution & Fulfilment (the "Move" module).
//
// First deliverable is the WAREHOUSING section, split into a deliberately small
// set of 3 input pages (cost-behaviour taxonomy: Fixed, Variable Labour,
// Variable Running). Inter-Site / Depot Transfers and Route to Market are
// planned as sibling sections — shown here (disabled) so the roadmap is visible.
const Distribution = () => {
  const navigate = useNavigate();
  const { selectedVersion } = useAppContext();

  // Store replenishment is config-gated: an FMCG manufacturer has no stores,
  // and a page you have to explain away is worse than a page that is not
  // there. Default OFF while the answer is unknown — a row that appears and
  // then vanishes reads as a bug, one that appears late reads as loading.
  const [storeOn, setStoreOn] = useState(false);
  useEffect(() => {
    if (!selectedVersion) return;
    let live = true;
    configService.getModuleConfig('MOD_4_MOVE', 'store_replenishment_enabled')
      .then((r) => { if (live) setStoreOn(r?.data?.value === 'Y'); })
      .catch(() => { /* leave it off — the page itself refuses the request too */ });
    return () => { live = false; };
  }, [selectedVersion]);

  return (
    <div className="h-full overflow-y-auto font-sans bg-slate-100 dark:bg-slate-900">
      <div className="max-w-6xl mx-auto px-6 py-10">

        <div className="mb-10 flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Distribution</h1>
            <p className="mt-2 text-base text-slate-500 dark:text-slate-400">
              Budget the cost of storing and moving product. Start with warehousing; inter-site transfers and route-to-market follow.
            </p>
          </div>
          <SubmitBudgetButton module="MOD_4_MOVE" />
        </div>

        <ListSection title="Warehousing">
          <ListRow
            icon={Anchor}
            title="Fixed Costs"
            description="Baseline fixed operating expenses — rent, insurance, security, leases — entered by formula (Rate × Qty) or manually per period."
            actions={<PrimaryButton onClick={() => navigate('/distribution/fixed-costs')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Users}
            title="Variable Labour"
            description="Shift headcount matrix per period — a volume plan only. Pay rates and cost are handled in the People module."
            actions={<PrimaryButton onClick={() => navigate('/distribution/variable-labour')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Activity}
            title="Operational Drivers"
            description="The quantities this site runs at, per period — pallets moved, floor area, energy, hours. Typed in, or linked live to the volume plan."
            actions={<PrimaryButton onClick={() => navigate('/distribution/operational-drivers')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Gauge}
            title="Variable Running Costs"
            description="Rate × driver, per period. One rate for the year, or twelve when a tariff or wage step lands mid-year."
            actions={<PrimaryButton onClick={() => navigate('/distribution/variable-running')}>Open</PrimaryButton>}
          />
        </ListSection>

        <ListSection title="Inter-Site Transfers">
          <ListRow
            icon={ArrowLeftRight}
            title="Inter-Depot Transport"
            description="Price the lanes between sites — a carrier per leg, own fleet or contracted, split by share. Volume comes from the planning module or is entered here."
            actions={<PrimaryButton onClick={() => navigate('/distribution/inter-depot')}>Open</PrimaryButton>}
          />
        </ListSection>

        <ListSection title="Route to Market">
          {/* Lives here, not under Budget Setup: who carries a lane at what
              rate is the distribution manager's own decision, made alongside
              the pages it feeds — unlike the posting catalogue below, which is
              a chart-of-accounts decision with a different owner. */}
          {/* Where and who first, then price. Zones, the customers mapped into
              them and the store calendar are one page because they are one
              decision — mapping a customer means reading the zone list and
              half the time adding to it. */}
          <ListRow
            icon={MapPin}
            title="Delivery Zones & Customers"
            description="The geographies you deliver into, each served from a depot at a distance; which zone each customer sits in and how big its drops are; and how often own stores are replenished."
            actions={<PrimaryButton onClick={() => navigate('/distribution/delivery-zones')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Truck}
            title="Secondary Lanes & Rates"
            description="Who carries what share of each lane and at what rate — own fleet or contracted, several rate components to a quote."
            actions={<PrimaryButton onClick={() => navigate('/distribution/secondary-lanes')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Truck}
            title="Customer Delivery"
            description="Depot to customer. Volume, drops, runs and km derive from the zone and carrier setup; the page shows the build and the cost to serve per customer and SKU."
            actions={<PrimaryButton onClick={() => navigate('/distribution/customer-delivery')}>Open</PrimaryButton>}
          />
          {storeOn && (
            <ListRow
              icon={Store}
              title="Store Replenishment"
              description="RDC to own stores, by cluster and temperature stream. Drops come off the delivery calendar; cages come off the store forecast."
              actions={<PrimaryButton onClick={() => navigate('/distribution/store-delivery')}>Open</PrimaryButton>}
            />
          )}
        </ListSection>

        {/* Configuration, not capture. Kept last and named for what it is: the
            decisions made once for every site, which the pages above inherit. */}
        <ListSection title="Budget Setup">
          {/* First in the section because it is the one setting that moves
              every other page at once — and the only place a single number
              reprices both inter-depot and secondary. */}
          <ListRow
            icon={Fuel}
            title="Price Indices"
            description="Diesel, and anything else volatile enough that one entry has to move everything. Change the price by month and every linked rate across inter-depot and secondary moves with it, each in proportion to how much of that rate is the commodity."
            actions={<PrimaryButton onClick={() => navigate('/distribution/budget-setup/price-indices')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={SlidersHorizontal}
            title="Variable Expenses"
            description="The catalogue of variable expense lines and where each posts — GL account across all sites, with a default cost centre and cost driver. The only place a line is created."
            actions={<PrimaryButton onClick={() => navigate('/distribution/budget-setup/variable-expenses')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={ArrowLeftRight}
            title="Inter-Depot Posting"
            description="Where inter-depot transport lands in the ledger — a GL account and cost centre per site, narrowed by lane or carrier type where it differs. The only place the posting is decided."
            actions={<PrimaryButton onClick={() => navigate('/distribution/budget-setup/transfer-posting')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={ArrowLeftRight}
            title="Secondary Posting"
            description="Where customer delivery and store replenishment land in the ledger — a GL account and cost centre per origin site, narrowed by zone or carrier type where it differs."
            actions={<PrimaryButton onClick={() => navigate('/distribution/budget-setup/secondary-posting')}>Open</PrimaryButton>}

          />
          <ListRow
            icon={Truck}
            title="Contractors"
            description="The transport contractors a lane can be outsourced to. Cross-site and shared by every budget version — only the rates are version-specific."
            actions={<PrimaryButton onClick={() => navigate('/distribution/budget-setup/contractors')}>Open</PrimaryButton>}
          />
        </ListSection>

      </div>
    </div>
  );
};

export default Distribution;
